<script setup lang="ts">
import { computed, ref } from 'vue'
import { formatBytes, useFileTransfer } from '@/composables/useFileTransfer'

const {
  files,
  phase,
  progress,
  copied,
  summary,
  shareUrl,
  addFiles,
  removeFile,
  transfer,
  copyLink,
  reset,
} = useFileTransfer()

const fileInput = ref<HTMLInputElement | null>(null)
const dragging = ref(false)

const shareLabel = shareUrl.replace(/^https?:\/\//, '')
const dropLabel = computed(() => {
  if (dragging.value) return 'Release to add'
  return files.value.length ? 'Add more files' : 'Upload files'
})

function onPick(event: Event) {
  const input = event.target as HTMLInputElement
  addFiles(input.files)
  // Clear the input so picking the same file again still fires `change`.
  input.value = ''
}

function onDragLeave(event: DragEvent) {
  // dragleave also fires when the pointer moves onto a child of the drop area.
  const area = event.currentTarget as HTMLElement
  if (event.relatedTarget instanceof Node && area.contains(event.relatedTarget)) return
  dragging.value = false
}

function onDrop(event: DragEvent) {
  dragging.value = false
  addFiles(event.dataTransfer?.files)
}
</script>

<template>
  <div class="landing">
    <header class="site-header">
      <div class="brand">
        <span class="brand__mark">d</span>
        Dropway
      </div>
      <nav class="site-nav">
        <a class="nav-link" href="#how">How it works</a>
        <a class="nav-link" href="#features">Features</a>
        <a class="nav-link" href="#pricing">Pricing</a>
        <a class="btn btn--white btn--sm" href="#">Log in</a>
      </nav>
    </header>

    <main>
      <section class="hero">
        <div class="hero__circle hero__circle--light" aria-hidden="true"></div>
        <div class="hero__circle hero__circle--dark" aria-hidden="true"></div>

        <div class="card">
          <template v-if="phase === 'idle'">
            <div
              class="dropzone-area"
              @dragover.prevent="dragging = true"
              @dragleave="onDragLeave"
              @drop.prevent="onDrop"
            >
              <button
                type="button"
                class="dropzone"
                :class="{ 'dropzone--dragging': dragging }"
                @click="fileInput?.click()"
              >
                <span class="dropzone__plus">+</span>
                <span class="dropzone__text">
                  <span class="dropzone__label">{{ dropLabel }}</span>
                  <span class="dropzone__hint">or drop them here · up to 5 GB free</span>
                </span>
              </button>
              <input ref="fileInput" type="file" multiple hidden @change="onPick" />

              <div v-if="files.length" class="file-list">
                <ul class="file-list__rows">
                  <li v-for="(file, i) in files" :key="i" class="file-row">
                    <span class="file-row__name">{{ file.name }}</span>
                    <span class="file-row__size">{{ formatBytes(file.size) }}</span>
                    <button
                      type="button"
                      class="file-row__remove"
                      :aria-label="`Remove ${file.name}`"
                      @click="removeFile(i)"
                    >
                      ×
                    </button>
                  </li>
                </ul>
                <div class="file-list__summary">{{ summary }}</div>
              </div>
            </div>

            <div class="transfer-form">
              <input class="field" type="email" placeholder="Email to" aria-label="Email to" />
              <input class="field" type="email" placeholder="Your email" aria-label="Your email" />
              <input class="field" placeholder="Title" aria-label="Title" />
              <textarea
                class="field field--message"
                placeholder="Message"
                aria-label="Message"
                rows="2"
              ></textarea>
              <button
                type="button"
                class="transfer-btn"
                :disabled="!files.length"
                @click="transfer"
              >
                Transfer
              </button>
            </div>
          </template>

          <div v-else-if="phase === 'uploading'" class="progress-panel">
            <div class="progress-panel__pct">{{ progress }}%</div>
            <div
              class="progress-bar"
              role="progressbar"
              aria-label="Upload progress"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="progress"
            >
              <div class="progress-bar__fill" :style="{ width: `${progress}%` }"></div>
            </div>
            <div class="progress-panel__title">Uploading {{ summary }}</div>
            <div class="progress-panel__note">
              Keep this tab open. We’ll email you when it’s done.
            </div>
          </div>

          <div v-else-if="phase === 'done'" class="done-panel">
            <span class="done-panel__icon">✓</span>
            <div class="done-panel__title">You’re done!</div>
            <div class="done-panel__text">
              Copy the link or check the recipient’s inbox. It expires in 7 days.
            </div>
            <div class="share-link">
              <span class="share-link__url">{{ shareLabel }}</span>
              <button type="button" class="share-link__copy" @click="copyLink">
                {{ copied ? 'Copied' : 'Copy link' }}
              </button>
            </div>
            <button type="button" class="link-btn" @click="reset">Send another</button>
          </div>
        </div>

        <div class="hero__copy">
          <h1 class="hero__title">Big files.<br />Zero friction.</h1>
          <p class="hero__lead">
            Drop anything — video, design files, whole folders — and send it with a link. No
            account, no compression, no waiting around.
          </p>
          <div class="hero__actions">
            <a class="btn btn--white btn--lg" href="#pricing">See plans</a>
            <a class="btn btn--outline-white btn--lg" href="#how">How it works</a>
          </div>
        </div>
      </section>

      <section id="how" class="section section--contained">
        <h2 class="section__title">Three steps. That’s it.</h2>
        <div class="steps">
          <div class="step">
            <span class="step__num">01</span>
            <h3 class="step__title">Add your files</h3>
            <p class="step__text">
              Drag them in or pick from your device. Folders keep their structure.
            </p>
          </div>
          <div class="step">
            <span class="step__num">02</span>
            <h3 class="step__title">Choose how to send</h3>
            <p class="step__text">
              Email it straight to someone or grab a link to share anywhere.
            </p>
          </div>
          <div class="step step--accent">
            <span class="step__num">03</span>
            <h3 class="step__title">Done</h3>
            <p class="step__text">
              You get a receipt when it’s downloaded. Links expire on your schedule.
            </p>
          </div>
        </div>
      </section>

      <section id="features" class="features">
        <div class="features__inner">
          <h2 class="section__title">Built for people who send real work.</h2>
          <div class="feature-list">
            <div class="feature">
              <h3 class="feature__title">Original quality</h3>
              <p class="feature__text">
                Files arrive exactly as you sent them — never compressed.
              </p>
            </div>
            <div class="feature">
              <h3 class="feature__title">Password &amp; expiry</h3>
              <p class="feature__text">Lock transfers and decide when links stop working.</p>
            </div>
            <div class="feature">
              <h3 class="feature__title">Encrypted end to end</h3>
              <p class="feature__text">Encrypted in transit and at rest, stored in the EU.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" class="section section--contained">
        <h2 class="section__title">Free to start.</h2>
        <div class="plans">
          <div class="plan">
            <span class="plan__name">Free</span>
            <span class="plan__price">€0</span>
            <p class="plan__text">5 GB per transfer · 7-day links</p>
            <a class="btn btn--outline-ink btn--plan" href="#">Start sending</a>
          </div>
          <div class="plan plan--featured">
            <span class="plan__name">Pro</span>
            <span class="plan__price">€12<span class="plan__period"> /month</span></span>
            <p class="plan__text">
              200 GB per transfer · passwords · custom expiry · download receipts
            </p>
            <a class="btn btn--white btn--plan" href="#">Try Pro free</a>
          </div>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <span>© 2026 Dropway</span>
      <nav class="site-footer__links" aria-label="Footer">
        <a href="#">Privacy</a>
        <a href="#">Terms</a>
        <a href="#">Help</a>
      </nav>
    </footer>
  </div>
</template>

<style scoped>
.landing {
  min-height: 100vh;
  background: #fff;
}

/* Header */
.site-header {
  position: absolute;
  inset: 0 0 auto;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px clamp(20px, 4vw, 48px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  font-size: 22px;
  color: #fff;
  letter-spacing: -0.02em;
}

.brand__mark {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #fff;
  color: var(--color-brand);
  display: grid;
  place-items: center;
  font-size: 18px;
}

.site-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.nav-link {
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  padding: 10px 14px;
  border-radius: 999px;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.14);
}

/* Buttons rendered as links */
.btn {
  border-radius: 999px;
  font-weight: 700;
}

.btn--sm {
  padding: 10px 18px;
  font-size: 15px;
}

.btn--lg {
  padding: 14px 24px;
  font-size: 16px;
}

.btn--plan {
  justify-self: start;
  margin-top: 8px;
  padding: 12px 22px;
  font-size: 16px;
}

.btn--white {
  background: #fff;
  color: var(--color-ink);
}

.btn--white:hover {
  background: var(--color-tint);
}

.btn--outline-white {
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.btn--outline-white:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.btn--outline-ink {
  color: var(--color-ink);
  border: 2px solid var(--color-ink);
}

.btn--outline-ink:hover {
  color: #fff;
  background: var(--color-ink);
}

/* Hero */
.hero {
  position: relative;
  min-height: 100vh;
  background: var(--color-brand);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: clamp(32px, 5vw, 80px);
  align-items: center;
  padding: 110px clamp(20px, 4vw, 48px) 60px;
  overflow: hidden;
}

.hero__circle {
  position: absolute;
  border-radius: 50%;
}

.hero__circle--light {
  right: -12vw;
  top: -10vw;
  width: 62vw;
  height: 62vw;
  background: var(--color-brand-light);
}

.hero__circle--dark {
  right: 8vw;
  bottom: -22vw;
  width: 40vw;
  height: 40vw;
  background: var(--color-brand-dark);
}

.hero__copy {
  position: relative;
  z-index: 2;
  color: #fff;
  max-width: 640px;
}

.hero__title {
  margin: 0;
  font-size: clamp(44px, 6.4vw, 92px);
  line-height: 0.98;
  font-weight: 800;
  letter-spacing: -0.035em;
}

.hero__lead {
  margin: 24px 0 0;
  font-size: clamp(17px, 1.5vw, 20px);
  line-height: 1.5;
  max-width: 46ch;
  color: var(--color-tint);
}

.hero__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 32px;
}

/* Transfer card */
.card {
  position: relative;
  z-index: 2;
  max-width: 440px;
  width: 100%;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(9, 25, 66, 0.35);
  overflow: hidden;
}

.dropzone-area {
  padding: 22px 22px 0;
}

.dropzone {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
  border: 2px dashed var(--color-border-dashed);
  border-radius: 16px;
  background: var(--color-surface);
  cursor: pointer;
  text-align: left;
}

.dropzone:hover,
.dropzone--dragging {
  border-color: var(--color-brand);
  background: var(--color-surface-hover);
}

.dropzone__plus {
  flex: none;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--color-brand);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
}

.dropzone__text {
  display: grid;
  gap: 2px;
}

.dropzone__label {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-ink);
}

.dropzone__hint {
  font-size: 14px;
  color: var(--color-muted);
}

.file-list {
  margin-top: 14px;
  max-height: 150px;
  overflow: auto;
  display: grid;
  /* minmax(0, 1fr) lets a long file name shrink and ellipsize instead of widening the row. */
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
}

.file-list__rows {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
}

.file-list__summary {
  font-size: 13px;
  color: var(--color-muted);
  padding: 2px 2px 0;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--color-surface);
}

.file-row__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
}

.file-row__size {
  font-size: 13px;
  color: var(--color-muted);
}

.file-row__remove {
  border: 0;
  background: none;
  color: var(--color-muted);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 6px;
}

.file-row__remove:hover {
  color: var(--color-ink);
  background: var(--color-tint-strong);
}

.transfer-form {
  padding: 16px 22px 22px;
  display: grid;
  gap: 10px;
}

.field {
  font-size: 15px;
  padding: 13px 14px;
  border: 0;
  border-bottom: 1.5px solid var(--color-border);
  outline: none;
  color: var(--color-ink);
}

.field:focus {
  border-bottom-color: var(--color-brand);
}

.field--message {
  resize: none;
}

.transfer-btn {
  margin-top: 8px;
  height: 52px;
  border: 0;
  border-radius: 999px;
  background: var(--color-brand);
  color: #fff;
  font-weight: 700;
  font-size: 17px;
  cursor: pointer;
}

.transfer-btn:hover:not(:disabled) {
  background: var(--color-brand-dark);
}

.transfer-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.progress-panel {
  padding: 48px 28px;
  display: grid;
  gap: 18px;
  justify-items: start;
}

.progress-panel__pct {
  font-size: 56px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--color-brand);
}

.progress-bar {
  width: 100%;
  height: 8px;
  border-radius: 99px;
  background: var(--color-tint);
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 99px;
}

.progress-panel__title {
  font-size: 16px;
  font-weight: 600;
}

.progress-panel__note {
  font-size: 14px;
  color: var(--color-muted);
}

.done-panel {
  padding: 40px 28px 28px;
  display: grid;
  gap: 14px;
}

.done-panel__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-brand);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 28px;
  font-weight: 700;
}

.done-panel__title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.done-panel__text {
  font-size: 15px;
  color: var(--color-muted);
}

.share-link {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 6px 6px 14px;
  border-radius: 999px;
  background: var(--color-surface);
  border: 1.5px solid var(--color-border);
}

.share-link__url {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.share-link__copy {
  border: 0;
  border-radius: 999px;
  background: var(--color-brand);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 16px;
  cursor: pointer;
}

.share-link__copy:hover {
  background: var(--color-brand-dark);
}

.link-btn {
  justify-self: start;
  border: 0;
  background: none;
  color: var(--color-brand);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  padding: 8px 0;
}

/* Sections */
.section {
  padding: clamp(64px, 9vw, 120px) clamp(20px, 4vw, 48px);
}

.section--contained {
  max-width: 1200px;
  margin: 0 auto;
}

.section__title {
  margin: 0;
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 48px;
}

.step {
  background: var(--color-surface);
  border-radius: 22px;
  padding: 32px;
  display: grid;
  gap: 12px;
  align-content: start;
}

.step--accent {
  background: var(--color-brand);
  color: #fff;
}

.step__num {
  font-size: 15px;
  font-weight: 800;
  color: var(--color-brand);
}

.step--accent .step__num {
  color: var(--color-on-brand-muted);
}

.step__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.step__text {
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: var(--color-soft);
}

.step--accent .step__text {
  color: var(--color-tint);
}

.features {
  background: var(--color-ink);
  color: #fff;
  padding: clamp(64px, 9vw, 120px) clamp(20px, 4vw, 48px);
}

.features__inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: clamp(32px, 5vw, 80px);
  align-items: start;
}

.feature-list {
  display: grid;
  gap: 28px;
}

.feature {
  display: grid;
  gap: 6px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.feature:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.feature__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.feature__text {
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: var(--color-on-ink-muted);
}

.plans {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-top: 48px;
}

.plan {
  border: 1.5px solid var(--color-border);
  border-radius: 22px;
  padding: 32px;
  display: grid;
  gap: 14px;
  align-content: start;
}

.plan--featured {
  background: var(--color-brand);
  color: #fff;
  border: 0;
}

.plan__name {
  font-size: 16px;
  font-weight: 700;
}

.plan__price {
  font-size: 44px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.plan__period {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-on-brand-muted);
}

.plan__text {
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: var(--color-soft);
}

.plan--featured .plan__text {
  color: var(--color-tint);
}

/* Footer */
.site-footer {
  border-top: 1.5px solid var(--color-border);
  padding: 28px clamp(20px, 4vw, 48px);
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 14px;
  color: var(--color-muted);
}

.site-footer__links {
  display: flex;
  gap: 20px;
}
</style>
